import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { useMemo, useState, type ReactNode } from 'react';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { useToast } from '~/shared/contexts/ToastContext';
import { useMatterMutations } from '~/modules/matters/useMatterMutations';
import { getHiredAndInvitedExpertIds } from '~/modules/matters/matterExpertUtils';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import { useMyMatterExpertsQuery } from '~/modules/matters/useMyMatterExpertsQuery';
import { AvatarImage } from '~/shared/components/AvatarImage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  matter: MatterViewModel;
}

const AddExpertsModal = ({ isOpen, onClose, matter }: Props) => {
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showToast } = useToast();
  const { addExpertToMatter, isAddingExpert } = useMatterMutations();
  const {
    experts: myMatterExperts,
    loading: myExpertsLoading,
    error: myExpertsError,
    refetch: refetchMyMatterExperts,
  } = useMyMatterExpertsQuery();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const excludedIds = useMemo(
    () => getHiredAndInvitedExpertIds(matter.matterExperts),
    [matter.matterExperts],
  );

  const availableExperts = useMemo(
    () => myMatterExperts.filter((expert) => !excludedIds.includes(expert.id)),
    [myMatterExperts, excludedIds],
  );

  const availableExpertIdSet = useMemo(
    () => new Set(availableExperts.map((expert) => expert.id)),
    [availableExperts],
  );

  /** Selection can lag after a refetch removes experts from myMatterExperts; only add ids still listed. */
  const selectedAvailableIds = useMemo(
    () => selectedIds.filter((id) => availableExpertIdSet.has(id)),
    [selectedIds, availableExpertIdSet],
  );

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const toggleExpert = (expertId: string) => {
    setSelectedIds((prev) =>
      prev.includes(expertId)
        ? prev.filter((id) => id !== expertId)
        : [...prev, expertId],
    );
  };

  const handleAdd = async () => {
    if (selectedAvailableIds.length === 0) {
      return;
    }
    try {
      await Promise.all(
        selectedAvailableIds.map((id) => addExpertToMatter(matter.id, id)),
      );
      showToast(
        selectedAvailableIds.length === 1 ? 'Expert added' : 'Experts added',
      );
      handleClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'add_experts_to_matter' },
        extra: { matterId: matter.id, expertIds: selectedAvailableIds },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to add experts. Please try again.';
      showToast(message, 'error');
    }
  };

  let addExpertsMainContent: ReactNode;
  if (myExpertsLoading) {
    addExpertsMainContent = (
      <EmptyBox>
        <Typography color="text.secondary">Loading experts…</Typography>
      </EmptyBox>
    );
  } else if (myExpertsError) {
    addExpertsMainContent = (
      <EmptyBox>
        <Stack alignItems="center" spacing={2}>
          <Typography color="error" component="p">
            {myExpertsError.message}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            An error occurred while loading experts. Please try again.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              void refetchMyMatterExperts();
            }}
          >
            Retry
          </Button>
        </Stack>
      </EmptyBox>
    );
  } else if (availableExperts.length === 0) {
    addExpertsMainContent = (
      <EmptyBox>
        <Typography color="text.secondary">
          No additional experts available to add (all are already assigned or on
          this matter).
        </Typography>
      </EmptyBox>
    );
  } else {
    addExpertsMainContent = (
      <StyledList disablePadding>
        {availableExperts.map((expert) => (
          <StyledListItemButton
            key={expert.id}
            selected={selectedAvailableIds.includes(expert.id)}
            onClick={() => toggleExpert(expert.id)}
          >
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={selectedAvailableIds.includes(expert.id)}
                tabIndex={-1}
                disableRipple
                aria-label={`Select expert ${expert.name}`}
              />
            </ListItemIcon>
            <ListItemAvatar>
              <AvatarImage
                avatarUrl={expert.avatarUrl}
                initialsLabel={expert.name}
                alt={`${expert.name} profile photo`}
              />
            </ListItemAvatar>
            <ListItemText primary={expert.name} secondary={expert.email} />
          </StyledListItemButton>
        ))}
      </StyledList>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isFullScreen}
    >
      <ModalHeader
        title="Add Experts"
        subtitle="Choose experts you've worked with to add to this matter."
        onClose={handleClose}
      />
      <StyledDialogContent>{addExpertsMainContent}</StyledDialogContent>
      <StyledDialogActions>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={selectedAvailableIds.length === 0 || isAddingExpert}
        >
          {isAddingExpert
            ? 'Adding…'
            : `Add${
                selectedAvailableIds.length > 0
                  ? ` (${selectedAvailableIds.length})`
                  : ''
              }`}
        </Button>
      </StyledDialogActions>
    </Dialog>
  );
};

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

const EmptyBox = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  textAlign: 'center',
}));

const StyledList = styled(List)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

export default AddExpertsModal;
